import {
  FC, useCallback, useMemo,
} from 'react';
import classNames from 'classnames';

import { useCurrencyContext } from 'src/common/providers/CurrencyProvider';
import { formatCurrencyLabel } from 'src/common/utils/currency';
import IconDS22 from '../IconDS22';
import styles from './CurrencySelectorInput.module.scss';
import Icon, { FlagIconsId } from '../Icon';
import getClassNames from '../../utils/getClassNames';

const topCurrencies = ['CAD', 'EUR', 'USD'];

export type CurrencySelectorInputProps = {
  /** The id to be used for the `select` */
  id?: string;
  renderAs?: 'div' | 'li';
  variant?: 'withFlag' | 'dropdownField';
  location?: 'topNav' | 'nestedMenu';
  background?: 'transparent' | 'white';
};

const CurrencySelectorInput: FC<CurrencySelectorInputProps> = ({
  id = 'currency', renderAs: Wrapper = 'div', variant, location, background = 'transparent',
}) => {
  const { currency, currencies, saveCurrency } = useCurrencyContext();
  const countryCode = useMemo(() => currency.code.slice(0, 2), [currency.code]) as FlagIconsId;
  const modifiers = useMemo(() => {
    if (variant === 'dropdownField') return `${variant} withFlag`;
    if (variant === 'withFlag') return `size-${formatCurrencyLabel(currency).length - 1} ${variant}`;
    return `size-${formatCurrencyLabel(currency).length - 1}`;
  }, [currency, variant]);

  const renderOption = useCallback(({ code, symbol }) => (
    <option
      key={code}
      value={code}
      disabled={code === currency.code}
      className={getClassNames(styles, 'option', variant)}
    >
      {formatCurrencyLabel({ code, symbol })}
    </option>
  ), [currency.code, variant]);

  const options = useMemo(
    () => currencies?.display_currencies?.map(renderOption),
    [currencies, renderOption],
  );

  const topCurrenciesOptions = useMemo(
    () => currencies?.display_currencies
      ?.filter(({ code }) => topCurrencies.includes(code))
      .map(renderOption),
    [currencies, renderOption],
  );

  const onChange = useCallback(({ target: { value: currencyValue } }) => {
    saveCurrency(currencyValue, true);
  }, [saveCurrency]);

  if (!currencies) {
    return null;
  }

  return (
    <Wrapper className={getClassNames(styles, 'root', classNames(modifiers, {
      topNav: location === 'topNav',

    }))}
    >
      <label
        htmlFor={id}
        className={getClassNames(styles, 'trigger', modifiers)}
      >
        {variant && (
          <Icon
            id={`flags/${countryCode}`}
            className={styles.flag}
          />
        )}
        <select
          aria-label="Currency selector"
          id={id}
          className={getClassNames(styles, 'dropdown', classNames(modifiers, {
            nestedMenu: location === 'nestedMenu',
            topNav: location === 'topNav',
            backgroundWhite: background === 'white',
          }))}
          value={currency.code}
          onChange={onChange}
        >
          <optgroup label="——————">
            {topCurrenciesOptions}
          </optgroup>
          <optgroup label="——————">
            {options}
          </optgroup>
        </select>
        {variant === 'dropdownField' && (
          <div className={getClassNames(styles, 'iconWrapper', variant)}>
            <IconDS22 id="caret-down" />
          </div>
        )}
      </label>
    </Wrapper>
  );
};

export default CurrencySelectorInput;
